import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchDashboard } from '@/api/dashboard'
import type { DashboardPayload } from '@/types/dashboard'

const BASE_POLL_MS = 12_000
const MAX_POLL_MS = 60_000

export function useDashboard(onError?: (msg: string) => void) {
  const [data, setData] = useState<DashboardPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [stale, setStale] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const failStreakRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const dataRef = useRef(data)
  dataRef.current = data

  const scheduleNext = useCallback((fn: () => void) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    const streak = failStreakRef.current
    const delay =
      streak <= 0
        ? BASE_POLL_MS
        : Math.min(MAX_POLL_MS, BASE_POLL_MS * Math.pow(2, Math.min(streak, 3)))
    timerRef.current = window.setTimeout(fn, delay)
  }, [])

  const refresh = useCallback(async (manual = false) => {
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    setRefreshing(true)
    try {
      const next = await fetchDashboard(40, ac.signal)
      setData(next)
      setStale(false)
      setLastError(null)
      setLoading(false)
      failStreakRef.current = 0
      return next
    } catch (e) {
      if ((e as Error).name === 'AbortError') return null
      const msg = String((e as Error).message || e)
      failStreakRef.current += 1
      setStale(true)
      setLastError(msg)
      if (!dataRef.current) setLoading(false)
      // Manual only — background failures surface via the connection banner
      if (manual) onErrorRef.current?.(msg)
      return null
    } finally {
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    const tick = () => {
      void refresh(false).finally(() => scheduleNext(tick))
    }
    void refresh(false).finally(() => scheduleNext(tick))
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      abortRef.current?.abort()
    }
  }, [refresh, scheduleNext])

  return { data, loading, stale, refreshing, lastError, refresh, setData }
}
